import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { Paper, Table, TableBody } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { ApiService, DocumentReferencesView, FolderView } from 'api';
import { tableHeadRows } from 'common/constants/documents';
import EnhancedTableHead from 'components/EnhancedTableHead';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import CurrentRequestedDocumentTableFolderRow from './CurrentRequestedDocumentTableFolderRow';
import CurrentRequestedDocumentTableMultipleSelectedBox from './CurrentRequestedDocumentTableMultipleSelectedBox';
import CurrentRequestedDocumentTableRow from './CurrentRequestedDocumentTableRow';
import EditFileDialog from './EditFileDialog';
import EditFolderDialog from './EditFolderDialog';
import RootFolderIconRow from './RootFolderIconRow';
import SendFileDialog from './SendFileDialog';
import {
  CurrentRequestedDocumentTablesProps,
  FileRequestMap,
  FolderFileMap,
  FolderOpenMap,
  OnSaveFunction,
} from './types';
import { useDropRow } from '../hooks/useDragAndDropRow';
import { useSortDocuments } from '../hooks/useSortDocuments';

const Root = styled('div')({
  width: '100%',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

const StyledTable = styled(Table)({
  minWidth: 750,
});

const EmptyFilesMessage = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: '0.8em',
  fontSize: '16px',
  color: theme.palette.text.primary,
}));


const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '&.drag-over': {
    outline: `1.5px dashed ${theme.palette.primary.main}`,
  },
}));

const UploadedDocumentsTable: FC<CurrentRequestedDocumentTablesProps> = ({ documents }) => {
  const {
    handleGetCurrentInformationRequests,
    currentDocuments,
    currentMeasurementDateId,
    refreshDocuments,
    downloadDocument,
    addFilesToFolder,
    updateSubFolder,
    measurementDates,
  } = useContext(InformationDashboard409AContext);

  const [draggedRow, setDraggedRow] = useState<DocumentReferencesView | null>(null);
  const [draggedSelectedFiles, setDraggedSelectedFiles] = useState<DocumentReferencesView[] | null>(null);
  const [selectedFile, setSelectedFile] = useState<DocumentReferencesView>();
  const [sendFile, setSendFile] = useState<DocumentReferencesView>();
  const [selectedFolder, setSelectedFolder] = useState<FolderView>();
  const [selectedFiles, setSelectedFiles] = useState<DocumentReferencesView[]>([]);

  const isEditFolderDialogOpen = useMemo(() => selectedFolder !== undefined, [selectedFolder]);
  const isEditFileDialogOpen = useMemo(() => selectedFile !== undefined, [selectedFile]);
  const isSendFileDialogOpen = useMemo(() => sendFile !== undefined, [sendFile]);

  const closeEditFolderDialog = useCallback(() => setSelectedFolder(undefined), [setSelectedFolder]);
  const closeEditFileDialog = useCallback(() => setSelectedFile(undefined), [setSelectedFile]);
  const closeSendFileDialog = useCallback(() => setSendFile(undefined), [setSendFile]);

  const updateFolder = useCallback(
    async (name: string, parentFolder: FolderView | undefined) => {
      if (selectedFolder?.id && selectedFolder.md_documents) {
        const data = {
          ...selectedFolder,
          parent_folder: parentFolder?.id ?? null,
          name,
        };
        const updatedFolder = await updateSubFolder(
          selectedFolder.md_documents.toString(),
          selectedFolder.id.toString(),
          data
        );
        if (updatedFolder) {
          refreshDocuments();
          closeEditFolderDialog();
        }
      }
    },
    [closeEditFolderDialog, refreshDocuments, selectedFolder, updateSubFolder]
  );

  const otherMeasurementDates = useMemo(
    () => measurementDates?.filter(date => date.id !== currentMeasurementDateId),
    [measurementDates, currentMeasurementDateId]
  );

  const updateFile = useCallback<OnSaveFunction>(
    async (fileUpdate, selectedFileRequestIds, deselectedFileRequestIds, referencesToAdd, referencesToRemove) => {
      const updatePromises = [];

      const requestUpdatePromise = ApiService.apiDocumentsUpdateRequestedFileUpdate(fileUpdate.id as number, {
        id: fileUpdate.id,
        filename: fileUpdate.filename,
        requested_file_ids_to_add: selectedFileRequestIds,
        requested_file_ids_to_remove: deselectedFileRequestIds,
      });
      updatePromises.push(requestUpdatePromise);

      if (referencesToAdd.length > 0 || referencesToRemove.length > 0) {
        const referenceUpdatePromise = ApiService.apiDocumentsDocumentReferenceObjectsCreate(
          currentMeasurementDateId?.toString() as string,
          {
            create_list: referencesToAdd,
            delete_list: referencesToRemove,
          }
        );
        updatePromises.push(referenceUpdatePromise);
      }

      await Promise.all(updatePromises);
      refreshDocuments();
      await handleGetCurrentInformationRequests();
      closeEditFileDialog();
    },
    [closeEditFileDialog, currentMeasurementDateId, handleGetCurrentInformationRequests, refreshDocuments]
  );

  // Clear selected files after successful drop
  const handleDropSuccess = useCallback((droppedFiles: DocumentReferencesView[]) => {
    // Clear selection after files have been moved to destination folder
    if (droppedFiles.length > 0) {
      // Remove all dropped files from selection using document_reference_id
      // Filter out undefined/null values to avoid removing files that weren't actually dropped
      const droppedReferenceIds = new Set(
        droppedFiles
          .map(file => file.document_reference_id)
          .filter((id): id is number => id !== undefined && id !== null)
      );
      setSelectedFiles(prev => prev.filter(file => !droppedReferenceIds.has(file.document_reference_id)));
    }
    // Clear drag states
    setDraggedSelectedFiles(null);
    setDraggedRow(null);
  }, []);

  const { handleDragOver, handleDrop, handleDragLeave } = useDropRow({
    draggedRow,
    draggedSelectedFiles: draggedSelectedFiles ?? undefined,
    addFilesToFolder,
    refreshDocuments,
    onDropSuccess: handleDropSuccess,
  });

  const fileRequestMap = useMemo(
    () =>
      documents.progress.reduce((acc, request) => {
        request.files.forEach(file => {
          const fileId = file.id ?? 0;
          if (!acc[fileId]) {
            acc[fileId] = [];
          }
          acc[fileId].push(request);
        });
        return acc;
      }, {} as FileRequestMap),
    [documents]
  );

  // we need to create an object that maps files in currentDocuments to corresponding folders in currentDocuments
  const folderFileMap = useMemo(() => {
    const map = currentDocuments?.files?.reduce((acc, file) => {
      const index = file.folder ?? 0;
      if (!acc[index]) {
        acc[index] = [];
      }
      acc[file.folder ?? 0].push(file);
      return acc;
    }, {} as FolderFileMap);
    return map;
  }, [currentDocuments]);

  const getInitialFolderStatusMap = useCallback(
    () =>
      currentDocuments?.folders?.reduce((acc, folder) => {
        if (folder.id) {
          acc[folder.id] = {
            open: false,
            parent: folder.parent_folder?.id,
          };
        }
        return acc;
      }, {} as FolderOpenMap) || {},
    [currentDocuments]
  );

  const [folderStatusMap, setFolderStatusMap] = useState<FolderOpenMap>(getInitialFolderStatusMap());

  const updateFolderStatusMap = useCallback(
    (folderId: number, childFolderIds: number[]) => {
      setFolderStatusMap(prevState => {
        if (!prevState[folderId]) {
          return getInitialFolderStatusMap();
        }
        const newState = { ...prevState };

        if (newState[folderId]) {
          const isMainFolderOpen = !newState[folderId].open;
          newState[folderId] = {
            ...newState[folderId],
            open: isMainFolderOpen,
          };

          // If main folder will be closed and have child folders, update the subfolder open status value
          if (!isEmpty(childFolderIds) && !isMainFolderOpen) {
            childFolderIds.forEach(childId => {
              if (newState[childId]) {
                newState[childId] = {
                  ...newState[childId],
                  open: isMainFolderOpen,
                };
              }
            });
          }
        }

        return newState;
      });
    },
    [getInitialFolderStatusMap]
  );

  const tableData = useMemo(() => {
    const topFolders = currentDocuments?.folders?.filter(folder => folder.parent_folder === null);
    const folders = topFolders?.map(folder => {
      //   find all the files related to this folder
      const childFolders = currentDocuments?.folders
        ?.filter(subFolder => subFolder.parent_folder?.id === folder.id)
        ?.map(subFolder => {
          const files = folderFileMap?.[subFolder.id ?? 0] || [];
          return {
            ...subFolder,
            files,
          };
        });
      return {
        ...folder,
        files: folderFileMap?.[folder?.id ?? 0] || [],
        childFolders: childFolders || [],
      };
    });
    const filesWithoutFolder = currentDocuments?.files?.filter(file => file.folder === null);
    return {
      folders,
      files: filesWithoutFolder,
    };
  }, [currentDocuments, folderFileMap]);

  const { sortDirection, orderBy, handleRequestSort, sortedFolders, sortedFiles } = useSortDocuments({
    folders: tableData?.folders,
    files: tableData?.files,
  });

  const folderList = sortedFolders;
  const sortedFilesWithoutFolder = sortedFiles;

  const areFilesOrFolders = useMemo(() => !isEmpty(tableData?.files) || !isEmpty(tableData?.folders), [tableData]);
  const areFolders = useMemo(() => !isEmpty(tableData?.folders), [tableData]);

  return (
    <>
      {selectedFiles.length > 1 && (
        <CurrentRequestedDocumentTableMultipleSelectedBox
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
        />
      )}
      {areFilesOrFolders ? (
        <Root>
          <StyledPaper>
            <StyledTable size="small" aria-label="documents table">
              <EnhancedTableHead
                onRequestSort={handleRequestSort}
                sortDirection={sortDirection}
                orderBy={orderBy}
                columns={tableHeadRows}
                files={currentDocuments?.files ?? []}
                selectedFiles={selectedFiles ?? []}
                setSelectedFiles={setSelectedFiles}
              />
              <StyledTableBody onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                {areFolders && <RootFolderIconRow isSubFolder={false} />}
                {sortedFilesWithoutFolder
                  && sortedFilesWithoutFolder.length > 0
                  && sortedFilesWithoutFolder.map(row => (
                    <CurrentRequestedDocumentTableRow
                      key={row.file.id}
                      row={row}
                      setSelectedFile={setSelectedFile}
                      setSendFile={setSendFile}
                      fileRequestMap={fileRequestMap}
                      setDraggedRow={setDraggedRow}
                      setDraggedSelectedFiles={setDraggedSelectedFiles}
                      downloadDocument={downloadDocument}
                      showRow
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      rowClass="root"
                    />
                  ))}
              </StyledTableBody>
              {currentDocuments
                && folderList?.map(folderRow => (
                  <TableBody key={folderRow.id}>
                    <CurrentRequestedDocumentTableFolderRow
                      folder={folderRow}
                      refreshDocuments={refreshDocuments}
                      addFilesToFolder={addFilesToFolder}
                      toggleFolder={updateFolderStatusMap}
                      isFolderOpen={folderStatusMap[folderRow.id as number]?.open}
                      draggedRow={draggedRow}
                      draggedSelectedFiles={draggedSelectedFiles ?? undefined}
                      onDropSuccess={handleDropSuccess}
                      setSelectedFolder={setSelectedFolder}
                      showFolder
                      currentDocumentsId={currentDocuments.id}
                      folderStatusMap={folderStatusMap}
                    />
                    {folderRow.files?.map(subRow => (
                      <CurrentRequestedDocumentTableRow
                        key={subRow.file.id}
                        row={subRow}
                        setSelectedFile={setSelectedFile}
                        setSendFile={setSendFile}
                        fileRequestMap={fileRequestMap}
                        setDraggedRow={setDraggedRow}
                        setDraggedSelectedFiles={setDraggedSelectedFiles}
                        downloadDocument={downloadDocument}
                        showRow={folderStatusMap[folderRow.id as number]?.open}
                        selectedFiles={selectedFiles}
                        setSelectedFiles={setSelectedFiles}
                        rowClass="folder"
                      />
                    ))}
                    {folderRow.childFolders?.map(childFolder => (
                      <React.Fragment key={childFolder.id}>
                        <CurrentRequestedDocumentTableFolderRow
                          folder={childFolder}
                          refreshDocuments={refreshDocuments}
                          addFilesToFolder={addFilesToFolder}
                          draggedRow={draggedRow}
                          draggedSelectedFiles={draggedSelectedFiles ?? undefined}
                          onDropSuccess={handleDropSuccess}
                          toggleFolder={updateFolderStatusMap}
                          showFolder={folderStatusMap[folderRow.id as number]?.open}
                          isFolderOpen={folderStatusMap[childFolder.id as number]?.open}
                          setSelectedFolder={setSelectedFolder}
                          currentDocumentsId={currentDocuments.id}
                          folderStatusMap={folderStatusMap}
                        />
                        {childFolder.files?.map(subRow => (
                          <CurrentRequestedDocumentTableRow
                            row={subRow}
                            key={subRow.file.id}
                            fileRequestMap={fileRequestMap}
                            setSelectedFile={setSelectedFile}
                            setSendFile={setSendFile}
                            setDraggedRow={setDraggedRow}
                            setDraggedSelectedFiles={setDraggedSelectedFiles}
                            downloadDocument={downloadDocument}
                            showRow={
                              folderStatusMap[childFolder.id as number]?.open
                              && folderStatusMap[folderRow.id as number]?.open
                            }
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            rowClass="subFolder"
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                ))}
            </StyledTable>
          </StyledPaper>
        </Root>
      ) : (
        <EmptyFilesMessage>No documents have been uploaded yet</EmptyFilesMessage>
      )}
      {selectedFile && (
        <EditFileDialog
          open={isEditFileDialogOpen}
          onClose={closeEditFileDialog}
          file={selectedFile}
          fileRequests={documents.progress}
          fileRequestMap={fileRequestMap}
          onSave={updateFile}
        />
      )}
      {sendFile && otherMeasurementDates && (
        <SendFileDialog
          open={isSendFileDialogOpen}
          onClose={closeSendFileDialog}
          file={sendFile}
          measurementDates={otherMeasurementDates}
        />
      )}
      {selectedFolder && (
        <EditFolderDialog
          open={isEditFolderDialogOpen}
          onClose={closeEditFolderDialog}
          folder={selectedFolder}
          onSave={updateFolder}
          folders={currentDocuments?.folders}
        />
      )}
    </>
  );
};

export default UploadedDocumentsTable;
